import { useQuery } from '@tanstack/react-query'
import { useAuth } from 'shared/context/AuthContext'
import { useStoProject } from './useStoProject'
import { stoQueries } from './queries/stoQueries'
import { getAuthState, type AuthState } from 'shared/utils/authState'
import type { ProjectDocument } from 'models/projectDocument'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import { formatUtcDate } from 'utils/formatUtcDate'

/** Tabs of the documents list. TEMPORARY split by back-office `fileType`, to be redone later. */
export type DocumentGroupId = 'offering' | 'other'

const OFFERING_TYPES = new Set([
  'offering-terms',
  'offering-document',
  'terms-conditions',
  'supplement',
  'country-disclosure',
])

export interface StoDocument {
  id: string
  group: DocumentGroupId
  title: string
  /** File extension, upper-cased. Derived from the filename — there is no stored type. */
  format?: string
  /** Pre-formatted, locale-independent. Never format dates at render (SSR hydration). */
  date?: string
  tooltip?: string
  fileName: string
  /** Back-office `publiclyDownloadable`: downloadable without login. */
  isPublic: boolean
  /** Whether this visitor can download it right now. */
  canDownload: boolean
  /** Locked for this visitor until they log in. */
  isRestricted: boolean
}

export interface DocumentGroup {
  id: DocumentGroupId
  documents: StoDocument[]
}

export interface DocumentsView {
  documents: StoDocument[]
  /** One tab each, in this order; a group without documents is left out. */
  groups: DocumentGroup[]
  /** Only once we know nobody is signed in, and only if some document needs login. */
  showLoginPrompt: boolean
}

export const formatDocumentDate = formatUtcDate

/** `Terms_and_conditions.pdf` -> `PDF`. */
export const formatDocumentFormat = (fileName: string | undefined): string | undefined => {
  const extension = fileName?.split('.').pop()
  if (!extension || extension === fileName) return undefined
  return extension.toUpperCase()
}

const documentTitle = (document: ProjectDocument): string =>
  document.title?.trim() ||
  (document.fileName ?? '')
    .replace(/\.[^.]+$/, '')
    .replace(/_/g, ' ')
    .trim()

export const toStoDocuments = (documents: ProjectDocument[] | undefined, authState: AuthState): StoDocument[] =>
  (documents ?? [])
    // Back-office decides what belongs on this page; absent means legacy-visible.
    .filter((document) => document.showOnSTOPage !== false)
    .map((document) => {
      const isPublic = document.publiclyDownloadable === true
      return {
        id: document._id ?? document.fileName ?? '',
        // Unknown or missing types (pitch deck, other, templates) go under Other.
        group: OFFERING_TYPES.has(document.fileType ?? '') ? ('offering' as const) : ('other' as const),
        title: documentTitle(document),
        format: formatDocumentFormat(document.fileName),
        date: formatDocumentDate(document.createdAt),
        tooltip: document.tooltip || undefined,
        fileName: document.fileName ?? '',
        isPublic,
        canDownload: isPublic || authState === 'authenticated',
        // Never while auth is resolving, so a signed-in visitor never sees it flash.
        isRestricted: !isPublic && authState === 'anonymous',
      }
    })
    .filter((document) => document.id && document.title)

const GROUP_ORDER: DocumentGroupId[] = ['offering', 'other']

export const toDocumentGroups = (documents: StoDocument[]): DocumentGroup[] =>
  GROUP_ORDER.map((id) => ({ id, documents: documents.filter((document) => document.group === id) })).filter(
    (group) => group.documents.length > 0,
  )

export const toDocumentsView = (documents: ProjectDocument[] | undefined, authState: AuthState): DocumentsView => {
  const stoDocuments = toStoDocuments(documents, authState)
  return {
    documents: stoDocuments,
    groups: toDocumentGroups(stoDocuments),
    showLoginPrompt: stoDocuments.some((document) => document.isRestricted),
  }
}

export function useDocuments(): SectionState<DocumentsView> {
  const { user } = useAuth()
  const { project, projectId } = useStoProject()
  const query = useQuery(stoQueries.documents(projectId, project?.name))

  return resolveSectionState({
    isPending: query.isPending,
    isError: query.isError,
    data: toDocumentsView(query.data, getAuthState(user)),
    isEmpty: (view) => view.documents.length === 0,
  })
}
